import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import Slider from "react-slick";
import { motion } from "framer-motion";
import axios from "axios";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./Home.css";
import AdmissionDropdown from "../components/AdmissionDropdown";

const Home = () => {
  const [stats, setStats] = useState({
    institutions: 4,
    students: "5000+",
    faculty: "200+",
    years: "Since 1966",
  });
  const [testimonials, setTestimonials] = useState([]);
  const [institutions, setInstitutions] = useState([
    {
      id: 1,
      name: "Sri Vani Public School",
      description:
        "Comprehensive education with focus on overall development and modern teaching methods",
      icon: "📚",
      url: "/campuses/rajaji-nagar",
    },
    {
      id: 2,
      name: "Sri Vani International School",
      description:
        "International curriculum preparing students for global excellence and cultural diversity",
      icon: "🌍",
      url: "/campuses/rajaji-nagar",
    },
    {
      id: 3,
      name: "Sri Vani High School",
      description:
        "Secondary education with emphasis on academic excellence and holistic development",
      icon: "📖",
      url: "/campuses/rajaji-nagar",
    },
    {
      id: 4,
      name: "Sri Vani Education Centre",
      description:
        "Specialized learning center offering personalized education and skill development",
      icon: "🎓",
      url: "/campuses/bashveshwarnagar",
    },
    {
      id: 5,
      name: "Sri Vani Education Centre School",
      description:
        "Integrated schooling with focus on practical learning and vocational skills",
      icon: "🏫",
      url: "/campuses/macholli",
    },
    {
      id: 6,
      name: "Sri Vani PU and Degree College",
      description:
        "Higher education institution offering pre-university and degree programs",
      icon: "🎯",
      url: "/academics/pu-college",
    },
    {
      id: 7,
      name: "Sri Vani School",
      description:
        "Established educational institution dedicated to academic and personal growth",
      icon: "✨",
      url: "/campuses/naavruja",
    },
  ]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [statsRes, testimonialsRes, institutionsRes] = await Promise.all([
        axios.get("/api/stats").catch((err) => ({ data: stats })),
        axios.get("/api/testimonials").catch((err) => ({ data: testimonials })),
        axios.get("/api/institutions").catch((err) => ({ data: institutions })),
      ]);

      setStats(statsRes.data);
      setTestimonials(testimonialsRes.data);
      setInstitutions(institutionsRes.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching data:", error);
      setLoading(false);
    }
  };

  // Hero Slider Settings
  const heroSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    fade: true,
    arrows: false,
  };

  // Testimonial Slider Settings
  const testimonialSettings = {
    dots: true,
    infinite: true,
    speed: 1000,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: true,
    autoplaySpeed: 5000,
    arrows: true,
    responsive: [
      {
        breakpoint: 768,
        settings: {
          slidesToShow: 1,
          arrows: false,
        },
      },
    ],
  };

  // Hero slides with video option
  // To use video: Change type to 'video' and update the media path
  const heroSlides = [
    {
      type: "video",
      title: "Welcome to Sri Vani Education",
      subtitle: "Excellence in Education Since 1966",
      media: "/videos/school-video.mp4", // Place your video in public/videos folder
    },
  ];

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  return (
    <div className="home">
      {/* Hero Slider */}
      <section className="hero-section">
        <Slider {...heroSettings}>
          {heroSlides.map((slide, index) => (
            <div key={index} className="hero-slide">
              {slide.type === "video" ? (
                <div className="hero-video-container">
                  <video className="hero-video" autoPlay muted loop playsInline poster="/images/hero-poster.jpg">
                    <source src={slide.media} type="video/mp4" />
                    Your browser does not support the video tag.
                  </video>
                  <div className="hero-overlay">
                    <div className="container">
                      <motion.div
                        className="hero-content"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                      >
                        <h1>{slide.title}</h1>
                        <p>{slide.subtitle}</p>
                        <div className="hero-buttons">
                          <Link to="/about" className="btn btn-primary">
                            Learn More
                          </Link>
                          <AdmissionDropdown label="Apply Now" className="btn btn-outline" />
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              ) : (
                <div
                  className="hero-image"
                  style={{ backgroundImage: `url(${slide.media})` }}
                >
                  <div className="hero-overlay">
                    <div className="container">
                      <motion.div
                        className="hero-content"
                        initial={{ opacity: 0, y: 50 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                      >
                        <h1>{slide.title}</h1>
                        <p>{slide.subtitle}</p>
                        <div className="hero-buttons">
                          <Link to="/about" className="btn btn-primary">
                            Learn More
                          </Link>
                          <AdmissionDropdown label="Apply Now" className="btn btn-outline" />
                        </div>
                      </motion.div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </Slider>
      </section>

      {/* Vision Section */}
      <section className="vision-section section">
        <div className="container">
          <motion.div
            className="vision-content"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="section-title">Our Vision</h2>
            <p className="vision-text">
              "Simplicity with utility and an affordable world class education
              that aims at integrated learning for life."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="mission-section section">
        <div className="container">
          <motion.div
            className="mission-content"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <h2 className="section-title">Our Mission</h2>
            <p className="mission-text">
              "To be an institution that fosters{" "}
              <strong>inquiring minds</strong> in a{" "}
              <strong>nurturing holistic environment</strong> centered around
              the <strong>learner</strong>; who shall be a{" "}
              <strong>contributing member of the society and world</strong> at
              large with <strong>freedom and responsibility</strong> in action
              and speech of mutual self-respect and value for a{" "}
              <strong>harmonious purposeful living</strong>."
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <div className="stat-number">{stats?.institutions || 4}</div>
              <div className="stat-label">Institutions</div>
            </motion.div>
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <div className="stat-number">{stats?.students || "5000+"}</div>
              <div className="stat-label">Students</div>
            </motion.div>
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <div className="stat-number">{stats?.faculty || "200+"}</div>
              <div className="stat-label">Faculties</div>
            </motion.div>
            <motion.div
              className="stat-card"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.3 }}
            >
              <div className="stat-number">{stats?.years || "Since 1966"}</div>
              <div className="stat-label">Legacy</div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Institutions Section */}
      <section className="institutions-section section">
        <div className="container">
          <h2 className="section-title">Our Institutions</h2>
          <p className="section-subtitle">
            We offer world-class education through multiple curricula to suit
            every student's needs
          </p>
          <div className="grid grid-4">
            {institutions.map((institution, index) => (
              <motion.div
                key={institution.id}
                className="card institution-card"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <div className="institution-icon">{institution.icon}</div>
                <h3>{institution.name}</h3>
                <p>{institution.description}</p>
                <Link to={institution.url} className="btn btn-outline">
                  Learn More
                </Link>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section section">
        <div className="container">
          <h2 className="section-title">What People Say</h2>
          <p className="section-subtitle">
            Hear from our students, parents, and alumni about their experiences
          </p>
          <Slider {...testimonialSettings} className="testimonials-slider">
            {testimonials.map((testimonial) => (
              <div key={testimonial.id} className="testimonial-slide">
                <div className="testimonial-card">
                  {testimonial.image && (
                    <div className="testimonial-image">
                      <img src={testimonial.image} alt={testimonial.name} />
                    </div>
                  )}
                  <div className="testimonial-content">
                    <p className="testimonial-message">
                      "{testimonial.message}"
                    </p>
                    <div className="testimonial-author">
                      <h4>{testimonial.name}</h4>
                      <p className="testimonial-role">{testimonial.role}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </Slider>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <motion.div
            className="cta-content"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2>Ready to Join Our Family?</h2>
            <p>
              Take the first step towards a bright future with Sri Vani
              Education
            </p>
            <AdmissionDropdown label="Apply for Admission" className="btn btn-secondary" />
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
